import React from "react";
import moment from "moment";

const getExperienceId = (exp) => exp?._id || exp?.id || null;

const getParentExperienceId = (exp) => {
  const parent = exp?.parentExperience;
  if (parent === null || parent === undefined || parent === "") return null;
  if (typeof parent === "object") return parent._id || parent.id || null;
  return parent;
};

const getNestedRelated = (exp) => {
  const nested = exp?.relatedExperiences || exp?.relatedExperience || [];
  return Array.isArray(nested) ? nested.filter(Boolean) : [];
};

// export const sortExperiencesLatestFirst = (a, b) => {
//   const aStart = a?.startDate ? new Date(a.startDate).getTime() : 0;
//   const bStart = b?.startDate ? new Date(b.startDate).getTime() : 0;
//   return bStart - aStart;
// };

const inheritEmployer = (parent, related) => {
  if (related?.employerName) return related;
  return { ...related, employerName: parent?.employerName || related?.employerName };
};

const roleEndTime = (role) =>
  role?.endDate ? new Date(role.endDate).getTime() : 0;

/** 0 current roles, 1 dated roles (newest endDate first), 2 missing endDate last. */
const roleSortGroup = (role) => {
  if (role?.currentRole) return 0;
  if (!role?.endDate) return 2;
  return 1;
};

const parentComesBefore = (parent, child) => {
  const parentGroup = roleSortGroup(parent);
  const childGroup = roleSortGroup(child);
  if (parentGroup !== childGroup) return parentGroup < childGroup;
  return roleEndTime(parent) > roleEndTime(child);
};

/** Insert only the parent. Children stay in their existing order. */
const insertParentByEndDate = (parent, children) => {
  const list = (Array.isArray(children) ? children : []).filter(Boolean);
  if (!parent) return list;
  const index = list.findIndex((child) => parentComesBefore(parent, child));
  if (index === -1) return [...list, parent];
  return [...list.slice(0, index), parent, ...list.slice(index)];
};

export const groupExperiences = (experiences = []) => {
  const list = Array.isArray(experiences) ? experiences : [];
  const byId = {};
  list.forEach((exp) => {
    const id = getExperienceId(exp);
    if (id) byId[id] = exp;
  });

  const relatedIds = new Set();
  const childrenByParent = {};

  const addChild = (parentId, child) => {
    const childId = getExperienceId(child);
    if (!parentId || !child || childId === parentId) return;
    if (!childrenByParent[parentId]) childrenByParent[parentId] = {};
    if (childId) {
      childrenByParent[parentId][childId] = inheritEmployer(byId[parentId], child);
      relatedIds.add(childId);
    }
  };

  list.forEach((exp) => {
    getNestedRelated(exp).forEach((related) => {
      addChild(getExperienceId(exp), related);
    });
    const parentId = getParentExperienceId(exp);
    if (parentId) addChild(parentId, exp);
  });

  const parents = list.filter((exp) => {
    const id = getExperienceId(exp);
    if (getParentExperienceId(exp)) return false;
    if (id && relatedIds.has(id) && !getNestedRelated(exp).length) return false;
    return true;
  });

  const groups = parents.map((parent) => {
    const parentId = getExperienceId(parent);
    const children = Object.values(childrenByParent[parentId] || {});
    // .sort(sortExperiencesLatestFirst);
    const roles = insertParentByEndDate(parent, children);
    // .sort(sortExperiencesLatestFirst);
    return { parent, children, roles };
  });

  list.forEach((exp) => {
    const id = getExperienceId(exp);
    const parentId = getParentExperienceId(exp);
    if (parentId && !byId[parentId] && !relatedIds.has(id)) {
      groups.push({ parent: exp, children: [], roles: [exp] });
    }
  });

  // groups.sort((a, b) => {
  //   const latestStart = (group) =>
  //     Math.max(
  //       ...group.roles.map((role) =>
  //         role?.startDate ? new Date(role.startDate).getTime() : 0
  //       )
  //     );
  //   return latestStart(b) - latestStart(a);
  // });

  return groups;
};

const formatMonths = (count) =>
  `${count} ${count === 1 ? "Mth" : "Mths"}`;

const formatDuration = (startDate, endDate, isCurrent) => {
  if (!startDate) return "";
  const start = moment(startDate);
  const end = isCurrent || !endDate ? moment() : moment(endDate);
  if (!start.isValid() || !end.isValid()) return "";
  const months = Math.max(end.diff(start, "months"), 0);
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  if (years > 0 && remainingMonths > 0) {
    return `${years} yr${years > 1 ? "s" : ""} ${formatMonths(remainingMonths)}`;
  }
  if (years > 0) return `${years} yr${years > 1 ? "s" : ""}`;
  if (months < 1) return "< 1 Mth";
  return formatMonths(months);
};

const formatDateRange = (startDate, endDate, isCurrent, currentLabel = "Present") => {
  const start = startDate ? moment(startDate).format("MMM YYYY") : "----";
  const end = isCurrent
    ? currentLabel
    : endDate
    ? moment(endDate).format("MMM YYYY")
    : "Present";
  return `${start} – ${end}`;
};

const getCompanyRange = (roles = []) => {
  const datedRoles = roles.filter((role) => role?.startDate);
  if (!datedRoles.length) {
    return { startDate: null, endDate: null, currentRole: false };
  }
  const startDate = datedRoles.reduce((earliest, role) => {
    return !earliest || moment(role.startDate).isBefore(earliest)
      ? role.startDate
      : earliest;
  }, null);
  const currentRole = roles.some((role) => role?.currentRole);
  const endedRoles = datedRoles.filter((role) => role?.endDate);
  const endDate = currentRole
    ? null
    : endedRoles.reduce((latest, role) => {
        return !latest || moment(role.endDate).isAfter(latest)
          ? role.endDate
          : latest;
      }, null);
  return { startDate, endDate, currentRole };
};

const CompanyLogo = ({ name }) => {
  const initial = name?.trim()?.charAt(0)?.toUpperCase() || "C";
  return (
    <div className="experienceCompanyLogo" aria-hidden="true">
      {initial}
    </div>
  );
};

const getNamedList = (items) =>
  (items || []).map((item) => item?.name).filter(Boolean);

const maskPrivate = (value) => {
  if (typeof value === "number") value = value.toString();
  return value?.replace(/[^\s]/g, "x");
};

const getRoleTitle = (role) => {
  const jobTitle = role?.jobTitle?.name;
  const project = role?.projectName;
  if (jobTitle && project) return `${jobTitle} · ${project}`;
  return jobTitle || project || "No data";
};

const getIndustryNames = (role) => {
  if (Array.isArray(role?.industries)) return getNamedList(role.industries);
  return role?.industry?.name ? [role.industry.name] : [];
};

const RoleBlock = ({ role, isHidden }) => {
  const duration = formatDuration(
    role?.startDate,
    role?.endDate,
    role?.currentRole
  );
  const industries = getIndustryNames(role);
  const inductions = getNamedList(role?.inductions);
  const skills = getNamedList(role?.skills);
  const discipline = role?.discipline?.name;
  const refereeName = isHidden ? maskPrivate(role?.refereeName) : role?.refereeName;
  const refereePosition = isHidden
    ? maskPrivate(role?.refereePosition)
    : role?.refereePosition;
  const refereeContact = isHidden
    ? maskPrivate(role?.refereeContact)
    : role?.refereeContact;

  return (
    <div className="experienceRoleCopy">
      <div className="experienceRoleTitleRow">
        <p className="experienceRoleTitle">{getRoleTitle(role)}</p>
        <div className="experienceRoleMeta">
          <p className="experienceMeta">
            {formatDateRange(
              role?.startDate,
              role?.endDate,
              role?.currentRole,
              "Current role"
            )}
            {duration ? ` · ${duration}` : ""}
          </p>
        </div>
      </div>
      <div className="experienceRoleGrid">
        <div className="experienceRoleFacts">
          {industries.length > 0 && (
            <div className="experienceFactRow">
              <span className="experienceFactLabel">Industry</span>
              <span className="experienceFactValue">{industries.join(", ")}</span>
            </div>
          )}
          {discipline && (
            <div className="experienceFactRow">
              <span className="experienceFactLabel">Discipline</span>
              <span className="experienceFactValue">{discipline}</span>
            </div>
          )}
          {inductions.length > 0 && (
            <div className="experienceFactRow">
              <span className="experienceFactLabel">Inductions</span>
              <span className="experienceFactValue">{inductions.join(", ")}</span>
            </div>
          )}
        </div>
        {skills.length > 0 && (
          <div className="experienceSkills">
            <p className="experienceSideLabel">Skills · {skills.length}</p>
            <div className="experienceSkillList">
              {skills.map((name, index) => (
                <span className="experienceSkillChip" key={`${name}-${index}`}>
                  {name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="experienceRefereeRow">
        <div className="experienceRefereeItem">
          <p className="experienceRefereeLabel">Referee name</p>
          <p className={`experienceRefereeValue ${isHidden ? "text_blur" : ""}`}>
            {refereeName || "—"}
          </p>
        </div>
        <div className="experienceRefereeItem">
          <p className="experienceRefereeLabel">Referee position</p>
          <p className={`experienceRefereeValue ${isHidden ? "text_blur" : ""}`}>
            {refereePosition || "—"}
          </p>
        </div>
        <div className="experienceRefereeItem">
          <p className="experienceRefereeLabel">Referee email / phone</p>
          <p className={`experienceRefereeValue ${isHidden ? "text_blur" : ""}`}>
            {refereeContact || "—"}
          </p>
        </div>
      </div>
    </div>
  );
};

function JobPositionCard({ data, childExperiences = [], isHidden }) {
  const nestedRelated = getNestedRelated(data).map((related) =>
    inheritEmployer(data, related)
  );
  const mergedChildren = [...nestedRelated, ...(childExperiences || [])];
  const childMap = {};
  mergedChildren.forEach((child) => {
    const childId = getExperienceId(child);
    if (childId && childId !== getExperienceId(data)) {
      childMap[childId] = child;
    } else if (!childId) {
      childMap[`local-${mergedChildren.indexOf(child)}`] = child;
    }
  });
  const children = Object.values(childMap);
  // .sort(sortExperiencesLatestFirst);
  const roles = insertParentByEndDate(data, children);
  // .sort(sortExperiencesLatestFirst);
  const company =
    data?.employerName ||
    roles.find((role) => role?.employerName)?.employerName;
  const companyRange = getCompanyRange(roles);
  const companyDuration = formatDuration(
    companyRange.startDate,
    companyRange.endDate,
    companyRange.currentRole
  );
  const isSingleRole = roles.length === 1;

  return (
    <div
      className={`jobPositionCard linkedinExperienceItem ${
        isSingleRole ? "isSingleRole" : "isMultiRole"
      }`}
    >
      <div className="experienceHeader">
        <CompanyLogo name={company} />
        <div className="experienceHeaderContent">
          <div className="experienceHeaderTop">
            <p className="title">{company || "----"}</p>
            <div className="experienceHeaderDates">
              <p className="experienceMeta">
                {formatDateRange(
                  companyRange.startDate,
                  companyRange.endDate,
                  companyRange.currentRole
                )}
              </p>
              {companyDuration && (
                <p className="experienceHeaderDuration">
                  {isSingleRole ? companyDuration : `${companyDuration} total`}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="experienceRoleTimeline">
        {roles.map((role, index) => (
          <div
            className={`experienceRoleItem ${
              index === roles.length - 1 ? "isLast" : ""
            }`}
            key={getExperienceId(role) || index}
          >
            <div className="experienceRoleMarker" />
            <RoleBlock role={role} isHidden={isHidden} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default JobPositionCard;
